// Carregamento das fotos da compra no editor (tela "Importando imagens") com
// novas tentativas e um prazo total.
//
// Antes não havia prazo nem tratamento: uma falha ao resolver a URL ou ao
// baixar a foto deixava o totem para sempre na tela de carregando. Agora o
// editor tenta de novo só as fotos que faltam, por no máximo PRAZO_MS; passado
// o prazo, desiste e lança ErroCarregamentoFotos para o editor mostrar o modal
// de erro.
import { comTimeout } from "@/utils/promiseTimeout";

// Tempo máximo total em tentativa e erro.
export const PRAZO_CARREGAR_FOTOS_MS = 120000;
// Tempo máximo de cada tentativa (resolver URLs ou baixar as fotos), para
// sobrar prazo para tentar de novo.
export const TEMPO_MAXIMO_TENTATIVA_MS = 40000;
// Espera entre tentativas (a última se repete).
export const ESPERAS_ENTRE_TENTATIVAS_MS = [1000, 2000, 4000, 5000];

export class ErroCarregamentoFotos extends Error {
  constructor(mensagem, detalhes = {}) {
    super(mensagem);
    this.name = "ErroCarregamentoFotos";
    this.detalhes = detalhes;
  }
}

function descreverCausa(erro) {
  return (
    (erro && erro.detalhes && erro.detalhes.causa) ||
    (erro && (erro.code || erro.message)) ||
    String(erro)
  );
}

// `indices`: fotos que ainda não estão no canvas; `null` = todas as que
// `resolverUrls` devolver (a quantidade ainda não é conhecida).
// `resolverUrls()`: devolve a lista (por índice) com o `src` de cada foto.
// `carregarFoto(foto, index, { tentativa, timeoutMs })`: baixa e põe no canvas.
export async function carregarFotosComPrazo({
  indices,
  resolverUrls,
  carregarFoto,
  prazoMs = PRAZO_CARREGAR_FOTOS_MS,
  tempoMaximoTentativaMs = TEMPO_MAXIMO_TENTATIVA_MS,
  esperas = ESPERAS_ENTRE_TENTATIVAS_MS,
  agora = () => Date.now(),
  esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
}) {
  const inicio = agora();
  const restante = () => prazoMs - (agora() - inicio);
  // Limite da próxima operação: nunca passa do que sobra do prazo.
  const limite = () =>
    Math.max(1, Math.min(tempoMaximoTentativaMs, restante()));

  let urls = null;
  let pendentes = indices ? [...indices] : null;
  const causas = [];
  let tentativa = 0;

  while (pendentes === null || pendentes.length) {
    tentativa += 1;
    try {
      if (!urls) {
        urls = await comTimeout(Promise.resolve().then(resolverUrls), {
          ms: limite(),
          rotulo: "endereços das fotos",
        });
      }
      if (pendentes === null) pendentes = urls.map((foto, index) => index);
      const timeoutMs = limite();
      const falhas = (
        await Promise.all(
          pendentes.map((index) =>
            Promise.resolve()
              .then(() =>
                carregarFoto(urls[index], index, { tentativa, timeoutMs })
              )
              .then(
                () => null,
                (erro) => ({ index, erro })
              )
          )
        )
      ).filter(Boolean);
      falhas.forEach(({ index, erro }) =>
        causas.push(`foto ${index + 1}: ${descreverCausa(erro)}`)
      );
      pendentes = falhas.map((falha) => falha.index);
    } catch (erro) {
      causas.push(`endereços: ${descreverCausa(erro)}`);
    }

    if (pendentes !== null && !pendentes.length) break;

    const espera = esperas[Math.min(tentativa - 1, esperas.length - 1)];
    // Sem tempo para esperar e tentar de novo dentro do prazo: desiste.
    if (restante() <= espera) {
      throw new ErroCarregamentoFotos("Não foi possível carregar as fotos", {
        // null: nem a lista de fotos chegou.
        pendentes,
        tentativas: tentativa,
        etapa: urls ? "download" : "enderecos",
        causa: causas[causas.length - 1] || null,
        // As últimas causas bastam para o suporte; o histórico inteiro de 120s
        // de tentativas pesaria no documento da compra.
        causas: causas.slice(-10),
      });
    }
    await esperar(espera);
  }

  return { tentativas: tentativa };
}
